import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import { Delete, Close, AddCircleOutline } from "@mui/icons-material";
import { getGlobals } from "../../../Configs";
import { fAlertToast, hasNonEnterStatus } from "../../../Utility/Utilitys";
import CustomDatagrid from "../Components/CustomDatagrid";
import ItemFieldSearchModal from "../Components/ItemFieldSearchModal";
import BrandSearchModal from "../Components/BrandSearchModal";
import dayjs from "dayjs";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import SideNavHeader from "../Components/SideNavHeader";
import {
  PackingUnit_Buyer_getrowsAPI,
  PAP_Category_Delete,
  PAP_Category_Getrow,
  PAP_Category_Update,
} from "../../../Server/ApiV2";

const CategorySearchModal = ({
  open,
  onClose,
  selectedPromo,
  selectedPage,
  pageType,
  isMobile,
  isTablet,
  loadPages,
  loadSummary,
}) => {
  const vRdxUserCd = useSelector((state) => state.login.value);

  const inputRefs = useRef({}); // { [RowId]: { [fieldName]: HTMLElement } }

  const FIELD_ORDER = [
    "Category",
    "NoOfItems",
    "Brand",
    "Buyer",
    "Packing",
    "Narration",
  ];

  const setInputRef = (rowId, field, el) => {
    if (!el) return;
    if (!inputRefs.current[rowId]) inputRefs.current[rowId] = {};
    inputRefs.current[rowId][field] = el;
  };

  const focusField = (rowId, field) => {
    const el = inputRefs.current?.[rowId]?.[field];
    if (el) {
      el.focus();
      if (el.select) el.select();
    }
  };

  const focusNextField = (rowId, currentField) => {
    const idx = FIELD_ORDER.indexOf(currentField);
    if (idx === -1) return;
    const next = FIELD_ORDER[idx + 1];
    if (!next) return;
    setTimeout(() => focusField(rowId, next), 0);
  };

  const [categoryData, setCategoryData] = useState([]);
  const [packingList, setPackingList] = useState([]);
  const [buyerList, setBuyerList] = useState([]); // NEW
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [openSearchCatModal, setSearchCatModal] = useState(false);
  const [openSearchBrdModal, setSearchBrdModal] = useState(false);
  const [searchProps, setSearchProps] = useState({
    field: "",
    categoryCd: "",
    value: "",
  });

  const columns = [
    {
      key: "CategoryName",
      header: "Category",
      width: 140,
      render: (row) => (
        <TextField
          inputProps={{ tabIndex: 1 }}
          fullWidth
          autoComplete="off"
          size="small"
          value={row.CategoryName || ""}
          onChange={(e) => {
            const newValue = e.target.value;
            setCategoryData((prev) =>
              prev.map((r) =>
                r.RowId === row.RowId ? { ...r, CategoryName: newValue } : r
              )
            );
          }}
          onKeyDown={(e) => handleCategorySearch(e, "Category", row.RowId)}
          inputRef={(el) => setInputRef(row.RowId, "Category", el)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <SearchIcon
                  onClick={(e) =>
                    handleCategorySearch(e, "Category", row.RowId)
                  }
                />
              </InputAdornment>
            ),
          }}
        />
      ),
    },
    { key: "SubGrpName", header: "SubGroup", width: 80 },
    { key: "MainGrpName", header: "MainGroup", width: 80 },

    {
      key: "NoOfItems",
      header: "No.Of.Items",
      align: "center",
      width: 55,
      numeric: true,
      render: (row) => (
        <TextField
          size="small"
          variant="outlined"
          value={row.NoOfItems ?? ""}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "");
            setCategoryData((prev) =>
              prev.map((r) =>
                r.RowId === row.RowId ? { ...r, NoOfItems: val } : r
              )
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              focusNextField(row.RowId, "NoOfItems");
            }
            if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
              e.preventDefault();
            }
          }}
          inputRef={(el) => setInputRef(row.RowId, "NoOfItems", el)}
          autoComplete="off"
          inputProps={{
            min: 0,
            inputMode: "numeric",
            pattern: "[0-9]*",
            style: { textAlign: "center" },
            tabIndex: 2,
          }}
          sx={{ width: "100%" }}
        />
      ),
    },
    {
      key: "BrandName",
      header: "Brand",
      align: "left",
      width: 120,
      render: (row) => (
        <TextField
          inputProps={{ tabIndex: 3 }}
          fullWidth
          autoComplete="off"
          size="small"
          value={row.BrandName || ""}
          onChange={(e) => {
            const newValue = e.target.value;

            setCategoryData((prev) =>
              prev.map((r) => {
                if (r.RowId !== row.RowId) return r;
                if (newValue.trim() === "") {
                  return {
                    ...r,
                    BrandName: "",
                    Brand: "",
                  };
                }
                return {
                  ...r,
                  BrandName: newValue,
                };
              })
            );
          }}
          onKeyDown={(e) => handleBrandSearch(e, "Brand", row.RowId)}
          inputRef={(el) => setInputRef(row.RowId, "Brand", el)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <SearchIcon
                  onClick={(e) => handleBrandSearch(e, "Brand", row.RowId)}
                />
              </InputAdornment>
            ),
          }}
        />
      ),
    },
    {
      key: "BuyerName",
      header: "Buyer",
      align: "left",
      width: 150,
      render: (row) => renderBuyerCell(row),
    },
    {
      key: "PackingName",
      header: "Packing",
      align: "left",
      width: 100,
      render: (row) => renderPackingCell(row),
    },
    {
      key: "Narration",
      header: "Narration",
      width: 170,
      render: (row) => (
        <TextField
          size="small"
          fullWidth
          multiline
          value={row.Narration || ""}
          inputProps={{ tabIndex: 6 }}
          inputRef={(el) => setInputRef(row.RowId, "Narration", el)}
          onChange={(e) => {
            const newValue = e.target.value;
            setCategoryData((prev) =>
              prev.map((r) =>
                r.RowId === row.RowId ? { ...r, Narration: newValue } : r
              )
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
      ),
    },
    {
      key: "EnDt",
      header: "Entered Date",
      width: 70,
      render: (row) => (row.EnDt ? dayjs(row.EnDt).format("DD/MM/YYYY") : ""),
    },
    {
      key: "",
      header: "#",
      align: "center",
      width: 10,
      render: (row) => (
        <IconButton
          color="error"
          disabled={hasNonEnterStatus(selectedPromo)}
          onClick={(e) => {
            confirmAlert({
              title: "Delete",
              message: "Are you sure you want to delete.",
              buttons: [
                {
                  className: "alertBtn",
                  name: "SaveYes",
                  label: "Yes",
                  onClick: () => {
                    handleDeleteRow(row?.SrNo, row?.RowId);
                  },
                },
                {
                  className: "alertBtn",
                  label: "No",
                  onClick: () => "",
                },
              ],
            });
          }}
          size="small"
        >
          <Delete fontSize="small" />
        </IconButton>
      ),
    },
  ];


  const fLoadCategory = async () => {
    try {
      const response = await PAP_Category_Getrow({
        TransId: selectedPromo?.TransId || "",
        PageId: selectedPage?.PageId || "",
      });
      if (response.status === 200)
        setCategoryData(response?.data?.ResultSet ?? []);
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const handleAddRow = () => {
    setCategoryData((prev) => {
      const lastRowId =
        prev.length > 0 ? Math.max(...prev.map((r) => r.RowId || 0)) : 0;
      const newRow = {
        SrNo: 0,
        RowId: lastRowId + 1,
        Category: "",
        CategoryName: "",
        MainGrpName: "",
        SubGrpName: "",
        NoOfItems: "",
        Buyer: "",
        BuyerName: "",
        Brand: "",
        BrandName: "",
        Packing: "",
        Narration: "",
      };
      return [...prev, newRow];
    });
  };

  const handleDeleteRow = async (srNo, RowId) => {
    const sr = Number(srNo ?? 0);
    // If this is a newly added (unsaved) row, just remove it locally
    if (!sr) {
      setCategoryData((prev) => prev.filter((r) => r.RowId != RowId));
      fAlertToast("SUCCESS", "Category row removed");
      return;
    }
    try {
      const res = await PAP_Category_Delete({
        TransId: selectedPromo?.TransId || "",
        PageId: selectedPage?.PageId || "",
        SrNo: srNo || "",
      });
      if (res.status === 200) {
        fAlertToast("SUCCESS", "Category row deleted");
        await fLoadCategory();
        await loadPages();
        await loadSummary();
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const handleCategorySearch = (e, fieldName, RowId) => {
    const row = categoryData.find((r) => r.RowId === RowId);
    const typed = e?.target?.value ?? row?.CategoryName ?? "";

    if (e.key === "Enter" || e._reactName === "onClick") {
      setSearchProps({ field: fieldName, categoryCd: "", value: typed });
      setSearchCatModal(true);
      setSelectedRowId(RowId);
      setTimeout(() => {
        document.getElementById("anyData")?.focus();
      }, 500);
    }
    if (e.key === "Backspace") {
      setCategoryData((prev) =>
        prev.map((r) =>
          r.RowId === RowId
            ? {
              ...r,
              Category: "",
              MainGrpName: "",
              SubGrpName: "",
              MainGrp: "",
              SubGrp: "",
            }
            : r
        )
      );
      setSearchProps((prev) => ({ ...prev, value: "" }));
    }
  };
  const handleBrandSearch = (e, fieldName, RowId) => {
    const row = categoryData.find((r) => r.RowId === RowId);
    const typed = e?.target?.value ?? row?.BrandName ?? "";
    const category = row?.Category ?? "";
    if (e.key === "Enter" || e._reactName === "onClick") {
      setSearchProps({ field: fieldName, categoryCd: category, value: typed });
      setSearchBrdModal(true);
      setSelectedRowId(RowId);
      setTimeout(() => {
        document.getElementById("anyData")?.focus();
      }, 500);
    }
    if (e.key === "Backspace") {
      setCategoryData((prev) =>
        prev.map((r) =>
          r.RowId === RowId
            ? {
              ...r,
              Brand: "",
            }
            : r
        )
      );
      setSearchProps((prev) => ({ ...prev, value: "" }));
    }
  };

  const getOptId = (opt) => String(opt?.UId ?? opt?.UID ?? "");
  const getOptLabel = (opt) => String(opt?.Name1 ?? "");

  const renderPackingCell = (row) => {
    const selectedOption =
      packingList.find((o) => getOptId(o) === String(row.Packing ?? "")) ||
      null;

    return (
      <Autocomplete
        size="small"
        fullWidth
        options={packingList}
        value={selectedOption}
        getOptionLabel={(opt) => getOptLabel(opt)}
        isOptionEqualToValue={(o, v) => getOptId(o) === getOptId(v)}
        onChange={(_, newValue) => {
          const uid = newValue ? getOptId(newValue) : "";
          const label = newValue ? getOptLabel(newValue) : "";
          setCategoryData((prev) =>
            prev.map((r) =>
              r.RowId === row.RowId
                ? {
                  ...r,
                  Packing: uid,
                  PackingName: label,
                }
                : r
            )
          );
          focusNextField(row.RowId, "Packing");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select packing"
            inputRef={(el) => setInputRef(row.RowId, "Packing", el)}
            inputProps={{
              ...params.inputProps,
              tabIndex: 5,
              style: { fontSize: "0.85rem" },
            }}
          />
        )}
      />
    );
  };

  const renderBuyerCell = (row) => {
    const selectedOption =
      buyerList.find((o) => getOptId(o) === String(row.Buyer ?? "")) || null;

    return (
      <Autocomplete
        size="small"
        fullWidth
        options={buyerList}
        value={selectedOption}
        getOptionLabel={(opt) => getOptLabel(opt)}
        isOptionEqualToValue={(o, v) => getOptId(o) === getOptId(v)}
        onChange={(_, newValue) => {
          const uid = newValue ? getOptId(newValue) : "";
          const label = newValue ? getOptLabel(newValue) : "";
          setCategoryData((prev) =>
            prev.map((r) =>
              r.RowId === row.RowId
                ? {
                  ...r,
                  Buyer: uid,
                  BuyerName: label,
                }
                : r
            )
          );
          focusNextField(row.RowId, "Buyer");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select buyer"
            inputRef={(el) => setInputRef(row.RowId, "Buyer", el)}
            inputProps={{
              ...params.inputProps,
              tabIndex: 4,
              style: { fontSize: "0.85rem" },
            }}
          />
        )}
      />
    );
  };

  const isBlank = (v) =>
    v === undefined || v === null || String(v).trim() === "";

  const handleSave = async () => {
    const errors = [];
    categoryData.forEach((r, idx) => {
      const missing = [];
      if (isBlank(r.Category)) missing.push("Category");
      const qty = Number(r.NoOfItems);
      if (isBlank(r.NoOfItems) || Number.isNaN(qty) || qty < 0) {
        missing.push("No Of Items");
      }
      if (isBlank(r.Buyer)) missing.push("Buyer");
      if (missing.length) {
        errors.push(
          `In Row ${r.RowId ?? idx + 1} Missing Values: ${missing.join(
            ", "
          )} should not be empty`
        );
      }
    });
    if (errors.length) {
      fAlertToast("FAILED", errors[0]);
      return;
    }

    const payload = {
      TransId: String(selectedPromo?.TransId ?? ""),
      PageId: String(selectedPage?.PageId ?? ""),
      UserCd: vRdxUserCd?.UserID,
      detail: categoryData.map((r, i) => ({
        SrNo: Number(r.SrNo),
        Category: String(r.Category),
        Buyer: String(r.Buyer),
        Brand: String(r.Brand),
        Packing: String(r.Packing),
        NoOfItems: Number(r.NoOfItems),
        Narration: String(r.Narration ?? ""),
      })),
    };
    try {
      const res = await PAP_Category_Update(payload);
      if (res.status === 200) {
        fAlertToast("SUCCESS", "Categories saved successfully");
        await loadPages();
        await fLoadCategory();
        await loadSummary();
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  const fGetPackingAndBuyer = async () => {
    try {
      const result = await PackingUnit_Buyer_getrowsAPI();
      if (result?.status === 200) {
        setPackingList(result?.data?.PackingUnit ?? []);
        setBuyerList(result?.data?.Buyer ?? []);
      }
    } catch (err) {
      fAlertToast("FAILED", err?.response?.data?.message || err?.message);
    }
  };

  useEffect(() => {
    if (open) {
      fLoadCategory();
      fGetPackingAndBuyer();
    }
  }, [open]);

  return (
    <>
      <Dialog open={open} fullWidth fullScreen={isTablet} maxWidth="xl">

        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            py: 1,
            fontSize: "1rem",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Category Selection
          <IconButton
            onClick={() => {
              onClose();
              setCategoryData([]);
            }}
            sx={{
              color: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.15)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 1, p: 2 }}>
          <SideNavHeader selectedRow={selectedPromo} pageType={pageType} />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button
              variant="text"
              startIcon={<AddCircleOutline />}
              size="small"
              color="primary"
              onClick={handleAddRow}
              sx={{ textTransform: "none", fontWeight: 600, my: 0.5 }}
            >
              Add Category
            </Button>
          </Box>
          <CustomDatagrid
            pageType={pageType}
            columns={columns}
            rows={categoryData}
            dense={true}
            height={{
              xs: "42vh",
              sm: "48vh",
              md: "44vh",
            }}
            getRowId={(row) => row.RowId}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              gap: 1.5,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={hasNonEnterStatus(selectedPromo)}
              size="small"
            >
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ItemFieldSearchModal
        xTitle={"Choose Category"}
        open={openSearchCatModal}
        handleClose={() => {
          setSearchCatModal(false);
        }}
        searchProps={searchProps}
        onRowSelect={(selectedRow) => {
          setCategoryData((prev) =>
            prev.map((r) =>
              r.RowId === selectedRowId
                ? {
                  ...r,
                  CategoryName: selectedRow.Name,
                  Category: selectedRow.Code,
                  MainGrpName: selectedRow.MainGroupName,
                  SubGrpName: selectedRow.SubCategoryName,
                  MainGrp: selectedRow.MainGroupCode,
                  SubGrp: selectedRow.SubCategory,
                }
                : r
            )
          );
          setSearchCatModal(false);
          focusNextField(selectedRowId, "Category");
        }}
        anyData={searchProps.value}
        vMediaQuery={isMobile}
      />

      <BrandSearchModal
        xTitle={"Choose Brand"}
        open={openSearchBrdModal}
        handleClose={() => {
          setSearchBrdModal(false);
        }}
        onRowSelect={(selectedRow) => {
          setCategoryData((prev) =>
            prev.map((r) =>
              r.RowId === selectedRowId
                ? {
                  ...r,
                  BrandName: selectedRow.BrandName,
                  Brand: selectedRow.BrandCode,
                }
                : r
            )
          );

          setSearchBrdModal(false);

          focusNextField(selectedRowId, "Brand");
        }}
        anyData={searchProps.value}
        categoryCode={searchProps.categoryCd}
        vMediaQuery={isMobile}
      />
    </>
  );
};

export default CategorySearchModal;
